// Firm SOP knowledge base — CRUD UI for markdown SOPs (Substep 5-γ.2).
// Backs onto /api/firm-sops endpoints from 5-γ.1.
// Paralegal+ can view / author / edit; firm_admin+ can delete.

import React, { useCallback, useEffect, useState } from "react";
import { C, roleAtLeast } from "../constants.js";
import { apiFetch } from "../api.js";
import { SopBodyRenderer } from "../components/SopBodyRenderer.jsx";

const EMPTY_FORM = { title: "", category: "", tags: "", body: "", applicable_stages: [] };

// Substep 5-ζ.1: matter stages an SOP can be tagged for. Mirrors the
// STAGES list in app/services/stages.py. If stages ever go per-firm
// this list should come from an API endpoint instead of being hardcoded,
// but MCB + Clio share these 6 today and hardcoding keeps the UI fast.
const STAGES = [
  { key: "new_case",    label: "New Case" },
  { key: "treatment",   label: "Treatment" },
  { key: "demand",      label: "Demand" },
  { key: "mediation",   label: "Mediation" },
  { key: "litigation",  label: "Litigation" },
  { key: "settlement",  label: "Settlement" },
];
const STAGE_LABEL_BY_KEY = Object.fromEntries(STAGES.map(s => [s.key, s.label]));

export function FirmSopsPage({ authStatus, showNotification }) {
  const role = authStatus?.firm_user?.role || "pending";
  const canWrite = roleAtLeast(role, "paralegal");
  const canDelete = roleAtLeast(role, "firm_admin");

  const [sops, setSops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  // editor state: null=closed | {mode:'new'|'edit', form, id?}
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  // viewer state: {sop} — separate from editor because view is read-only
  const [viewing, setViewing] = useState(null);
  // Ask-your-SOPs chat state (Substep 5-γ.3)
  const [askQuestion, setAskQuestion] = useState("");
  const [askResult, setAskResult] = useState(null);       // { answer, citations, matched_count }
  const [askLoading, setAskLoading] = useState(false);
  // Category groups collapse state — set of category keys the user has closed.
  // Undefined categories share the "(none)" bucket. Grouping only kicks in when
  // no search / filter is active; otherwise the list renders flat.
  const [collapsedCats, setCollapsedCats] = useState(new Set());
  const toggleCat = (key) => setCollapsedCats(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("q", search.trim());
      if (categoryFilter) params.set("category", categoryFilter);
      const d = await apiFetch(`/api/firm-sops?${params.toString()}`);
      setSops(d.data || []);
    } catch (e) {
      if (e.status === 403) {
        showNotification?.("Only paralegals and above can view SOPs.", "warning");
      } else {
        showNotification?.(`Load failed: ${e.message}`, "error");
      }
      setSops([]);
    }
    setLoading(false);
  }, [search, categoryFilter, showNotification]);

  useEffect(() => { load(); }, [load]);

  // All categories seen across loaded SOPs, for the filter dropdown.
  const allCategories = [...new Set(sops.map(s => s.category).filter(Boolean))].sort();

  const openNew = () => {
    setEditor({ mode: "new", form: { ...EMPTY_FORM } });
  };

  const openEdit = async (sop) => {
    // Fetch full body — list view only carries a 200-char preview
    try {
      const full = await apiFetch(`/api/firm-sops/${sop.id}`);
      setEditor({
        mode: "edit",
        id: sop.id,
        form: {
          title: full.title || "",
          category: full.category || "",
          tags: full.tags || "",
          body: full.body || "",
          applicable_stages: Array.isArray(full.applicable_stages) ? full.applicable_stages : [],
        },
      });
    } catch (e) {
      showNotification?.(`Failed to load SOP: ${e.message}`, "error");
    }
  };

  const openView = async (sop) => {
    try {
      const full = await apiFetch(`/api/firm-sops/${sop.id}`);
      setViewing(full);
    } catch (e) {
      showNotification?.(`Failed to load SOP: ${e.message}`, "error");
    }
  };

  const save = async () => {
    if (!editor) return;
    const { form, mode, id } = editor;
    if (!form.title.trim() || !form.body.trim()) {
      showNotification?.("Title and body are required.", "warning");
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        body: form.body,
        category: form.category.trim() || null,
        tags: form.tags.trim() || null,
        // Empty list clears any prior tags server-side (stores NULL).
        applicable_stages: Array.isArray(form.applicable_stages) ? form.applicable_stages : [],
      };
      if (mode === "new") {
        await apiFetch("/api/firm-sops", {
          method: "POST",
          body: JSON.stringify(body),
        });
        showNotification?.(`SOP "${form.title}" created.`, "success");
      } else {
        await apiFetch(`/api/firm-sops/${id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        showNotification?.("SOP updated.", "success");
      }
      setEditor(null);
      await load();
    } catch (e) {
      showNotification?.(`Save failed: ${e.message}`, "error");
    }
    setSaving(false);
  };

  const uploadFile = async (file) => {
    if (!file) return;
    const ok = /\.(docx|pdf|txt|md|markdown)$/i.test(file.name);
    if (!ok) {
      showNotification?.("Only .docx, .pdf, .txt, or .md supported.", "warning");
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      showNotification?.("File too large (max 200 MB). Split the SOP into topical sections and upload each separately for better AI retrieval.", "warning");
      return;
    }
    // Warn on genuinely huge files — extraction takes 30-60s and the
    // original bytes won't be persisted (over 25 MB threshold).
    if (file.size > 25 * 1024 * 1024) {
      if (!window.confirm(
        `This is a large file (${(file.size / 1024 / 1024).toFixed(1)} MB). ` +
        `\n\n` +
        `• Text extraction may take up to a minute.\n` +
        `• The original file will NOT be stored (only the extracted text). ` +
        `Keep your local copy if you need it later.\n` +
        `• For better AI Q&A, consider splitting into smaller topical SOPs (10-20 pages each).\n\n` +
        `Upload anyway?`
      )) return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/firm-sops/upload", {
        method: "POST", body: fd, credentials: "same-origin",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || res.statusText);
      const suffix = data.original_stored
        ? ""
        : " (original file not stored — over 25 MB)";
      showNotification?.(
        `Uploaded "${data.title}" — ${data.body_length.toLocaleString()} chars extracted.${suffix}`,
        "success",
      );
      await load();
      // Auto-open the newly created SOP in the viewer so the user
      // can immediately verify extraction quality + tweak metadata.
      openView({ id: data.id });
    } catch (e) {
      showNotification?.(`Upload failed: ${e.message}`, "error");
    }
  };

  const askSops = async () => {
    const q = askQuestion.trim();
    if (!q || askLoading) return;
    setAskLoading(true);
    setAskResult(null);
    try {
      const res = await apiFetch("/api/firm-sops/ask", {
        method: "POST",
        body: JSON.stringify({ question: q }),
      });
      setAskResult(res);
    } catch (e) {
      showNotification?.(`Ask failed: ${e.message}`, "error");
    }
    setAskLoading(false);
  };

  const openCitation = async (citation) => {
    // Fetch full body then open the viewer modal — same code path
    // as clicking a list row.
    try {
      const full = await apiFetch(`/api/firm-sops/${citation.sop_id}`);
      setViewing(full);
    } catch (e) {
      showNotification?.(`Failed to load SOP: ${e.message}`, "error");
    }
  };

  const removeSop = async (sop) => {
    if (!window.confirm(`Delete SOP "${sop.title}"?\n\nThis cannot be undone.`)) return;
    try {
      const res = await fetch(`/api/firm-sops/${sop.id}`, {
        method: "DELETE", credentials: "same-origin",
      });
      if (!res.ok) {
        let msg = res.statusText;
        try { const j = await res.json(); msg = j.detail || msg; } catch {}
        if (res.status === 403) {
          showNotification?.("Only Firm Admins can delete SOPs.", "warning");
        } else {
          throw new Error(msg);
        }
        return;
      }
      showNotification?.("SOP deleted.", "success");
      await load();
    } catch (e) {
      showNotification?.(`Delete failed: ${e.message}`, "error");
    }
  };

  return React.createElement("div", { className: "fade-in", style: { padding: "32px 40px", maxWidth: "1100px" } },
    // Header
    React.createElement("div", { style: { marginBottom: "20px" } },
      React.createElement("h1", {
        style: { fontFamily: "'Playfair Display',serif", fontSize: "28px", fontWeight: 600, color: C.navy, marginBottom: "6px" }
      }, "Firm SOPs"),
      React.createElement("p", { style: { fontSize: "14px", color: C.textMuted } },
        "Your firm's standard-operating procedures, written in markdown. Referenced at document generation time so the AI knows how your firm does bankruptcy checks, intake, subrogation, etc."
      ),
    ),

    // Ask-your-SOPs panel (Substep 5-γ.3)
    React.createElement("div", {
      style: {
        marginBottom: "20px", background: "#fff", borderRadius: "10px",
        border: `1px solid ${C.warmGray}`, padding: "16px 18px",
      }
    },
      React.createElement("div", {
        style: {
          fontSize: "11px", color: C.textMuted, textTransform: "uppercase",
          letterSpacing: "0.08em", fontWeight: 600, marginBottom: "8px",
          display: "flex", alignItems: "center", gap: "6px",
        }
      }, "Ask your SOPs",
        React.createElement("span", {
          style: {
            fontSize: "9px", padding: "2px 6px", borderRadius: "3px",
            background: `${C.gold}20`, color: C.gold, fontWeight: 700,
          }
        }, "AI")
      ),
      React.createElement("div", {
        style: {
          display: "flex", gap: "8px",
        }
      },
        React.createElement("input", {
          type: "text",
          value: askQuestion,
          onChange: (e) => setAskQuestion(e.target.value),
          onKeyDown: (e) => { if (e.key === "Enter") askSops(); },
          placeholder: "e.g. How do we handle a bankruptcy check on a new client?",
          disabled: askLoading,
          style: {
            flex: 1, padding: "10px 14px", border: `1px solid ${C.warmGray}`,
            borderRadius: "6px", fontSize: "13px",
          }
        }),
        React.createElement("button", {
          onClick: askSops,
          disabled: askLoading || !askQuestion.trim(),
          style: {
            padding: "10px 18px", borderRadius: "6px", border: "none",
            background: (askLoading || !askQuestion.trim()) ? C.warmGray : C.gold,
            color: "#fff", fontSize: "13px", fontWeight: 600,
            cursor: (askLoading || !askQuestion.trim()) ? "default" : "pointer",
            whiteSpace: "nowrap",
          }
        }, askLoading ? "Thinking…" : "Ask"),
      ),

      // Result
      askResult && React.createElement("div", { style: { marginTop: "14px" } },
        // Answer
        React.createElement("div", {
          style: {
            padding: "12px 14px", background: C.warmWhite, borderRadius: "8px",
            fontSize: "13px", color: C.textDark, lineHeight: 1.6,
            whiteSpace: "pre-wrap", wordWrap: "break-word",
            border: `1px solid ${C.warmGray}`,
          }
        }, askResult.answer),

        // Citations
        askResult.citations && askResult.citations.length > 0 && React.createElement("div", { style: { marginTop: "10px" } },
          React.createElement("div", {
            style: {
              fontSize: "10px", color: C.textMuted, textTransform: "uppercase",
              letterSpacing: "0.08em", fontWeight: 600, marginBottom: "6px",
            }
          }, "Cited SOPs"),
          React.createElement("div", { style: { display: "flex", flexWrap: "wrap", gap: "6px" } },
            ...askResult.citations.map(c => React.createElement("button", {
              key: c.sop_id,
              onClick: () => openCitation(c),
              title: c.snippet,
              style: {
                padding: "4px 10px", borderRadius: "999px",
                border: `1px solid ${C.gold}60`,
                background: "#fff", color: C.navy, fontSize: "11px",
                fontWeight: 600, cursor: "pointer",
              }
            }, c.title + (c.category ? " · " + c.category : ""))),
          ),
        ),
      ),
    ),

    // Filter bar
    React.createElement("div", {
      className: "wrap-row",
      style: {
        display: "flex", gap: "10px", marginBottom: "16px",
        background: "#fff", padding: "10px 14px", borderRadius: "10px",
        border: `1px solid ${C.warmGray}`,
      }
    },
      React.createElement("input", {
        type: "search",
        value: search,
        onChange: (e) => setSearch(e.target.value),
        placeholder: "Search by title, body, tags, or category…",
        style: {
          flex: 1, padding: "8px 12px", border: `1px solid ${C.warmGray}`,
          borderRadius: "6px", fontSize: "13px",
        }
      }),
      React.createElement("select", {
        value: categoryFilter,
        onChange: (e) => setCategoryFilter(e.target.value),
        style: {
          padding: "8px 12px", border: `1px solid ${C.warmGray}`,
          borderRadius: "6px", fontSize: "13px", minWidth: "150px",
          background: "#fff", color: C.textDark, cursor: "pointer",
        }
      },
        React.createElement("option", { value: "" }, "All categories"),
        ...allCategories.map(c => React.createElement("option", { key: c, value: c }, c))
      ),
      canWrite && React.createElement("label", {
        title: "Upload a .docx / .pdf / .txt SOP file. Text is auto-extracted for AI Q&A; original file is kept for download.",
        style: {
          padding: "8px 16px", borderRadius: "6px",
          border: `1px solid ${C.navy}`, background: "#fff", color: C.navy,
          fontSize: "13px", fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
        }
      }, "Upload SOP file",
        React.createElement("input", {
          type: "file",
          accept: ".docx,.pdf,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown",
          style: { display: "none" },
          onChange: (e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; },
        }),
      ),
      canWrite && React.createElement("button", {
        onClick: openNew,
        style: {
          padding: "8px 16px", borderRadius: "6px", border: "none",
          background: C.gold, color: "#fff", fontSize: "13px",
          fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
        }
      }, "+ New SOP"),
    ),

    // List
    loading
      ? React.createElement("div", { style: { padding: "40px", color: C.textMuted, fontSize: "13px" } }, "Loading…")
      : sops.length === 0
        ? React.createElement("div", {
            style: {
              padding: "40px", textAlign: "center", color: C.textMuted,
              background: "#fff", borderRadius: "10px",
              border: `1px solid ${C.warmGray}`, fontSize: "13px",
            }
          },
            (search || categoryFilter)
              ? "No SOPs match those filters."
              : "No SOPs written yet. Click ",
            (search || categoryFilter)
              ? null
              : React.createElement("strong", null, "+ New SOP"),
            (search || categoryFilter) ? null : " to get started."
          )
        : (function renderList() {
            // Denser one-line row — no preview, no chars/date noise.
            // Click anywhere on the row to open the viewer.
            const renderRow = (sop, opts = {}) => React.createElement("div", {
              key: sop.id,
              style: {
                background: "#fff",
                borderTop: opts.inGroup ? `1px solid ${C.warmGray}` : `1px solid ${C.warmGray}`,
                borderRight: `1px solid ${C.warmGray}`,
                borderLeft: `1px solid ${C.warmGray}`,
                borderBottom: opts.isLastInGroup ? `1px solid ${C.warmGray}` : "none",
                borderRadius: opts.roundTop
                  ? (opts.roundBottom ? "8px" : "8px 8px 0 0")
                  : (opts.roundBottom ? "0 0 8px 8px" : "0"),
                padding: "10px 14px", cursor: "pointer",
                display: "flex", alignItems: "center", gap: "10px",
              },
              // On a phone this row has to stack. Everything after the
              // title is flexShrink:0, so the title — the only thing
              // that identifies the SOP — is the only part that gives,
              // and it collapsed to "Ho…" and "W…".
              className: "stack-row",
              onClick: () => openView(sop),
            },
              React.createElement("div", {
                style: { fontSize: "13px", fontWeight: 600, color: C.navy, flex: 1, minWidth: 0,
                         overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }
              }, sop.title),
              // Category chip only in flat mode (group header already shows it)
              !opts.inGroup && sop.category && React.createElement("span", {
                style: {
                  fontSize: "10px", padding: "2px 8px", borderRadius: "999px",
                  background: C.warmWhite, color: C.textMuted, fontWeight: 600, flexShrink: 0,
                }
              }, sop.category),
              // Stage chips (Substep 5-ζ.1) — pill-shaped, muted background
              // so they read as reference metadata, distinct from #tag chips.
              (sop.applicable_stages || []).map(k => STAGE_LABEL_BY_KEY[k] && React.createElement("span", {
                key: "st-" + k,
                style: {
                  fontSize: "10px", padding: "2px 8px", borderRadius: "999px",
                  background: `${C.gold}22`, color: C.textDark, fontWeight: 600, flexShrink: 0,
                },
                title: "Matter stage: " + STAGE_LABEL_BY_KEY[k],
              }, STAGE_LABEL_BY_KEY[k])),
              // Tag chips (kept — cheap, useful)
              sop.tags && sop.tags.split(",").map((t, i) => t.trim() && React.createElement("span", {
                key: "tg-" + i,
                style: {
                  fontSize: "10px", padding: "1px 6px", borderRadius: "4px",
                  background: `${C.navy}10`, color: C.navy, fontFamily: "monospace", flexShrink: 0,
                }
              }, "#" + t.trim())),
              sop.updated_at && React.createElement("span", {
                style: {
                  fontSize: "11px", color: C.textMuted, flexShrink: 0,
                  fontVariantNumeric: "tabular-nums",
                },
                title: new Date(sop.updated_at).toLocaleString(),
              }, "updated " + sop.updated_at.slice(0, 10)),
              React.createElement("div", {
                onClick: (e) => e.stopPropagation(),
                style: { display: "flex", gap: "4px", flexShrink: 0 }
              },
                canWrite && React.createElement("button", {
                  onClick: () => openEdit(sop),
                  style: {
                    padding: "3px 8px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                    background: "#fff", color: C.textDark, fontSize: "11px", cursor: "pointer",
                  }
                }, "Edit"),
                canDelete && React.createElement("button", {
                  onClick: () => removeSop(sop),
                  style: {
                    padding: "3px 8px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
                    background: "#fff", color: C.error, fontSize: "11px", cursor: "pointer",
                  }
                }, "Delete"),
              ),
            );

            // Flat list when the user is actively filtering — grouping hides matches.
            const isFiltering = !!(search.trim() || categoryFilter);
            if (isFiltering) {
              return React.createElement("div", {
                style: { display: "flex", flexDirection: "column" }
              },
                ...sops.map((sop, i) => renderRow(sop, {
                  roundTop: i === 0,
                  roundBottom: i === sops.length - 1,
                  isLastInGroup: i === sops.length - 1,
                }))
              );
            }

            // Grouped by category with collapsible section headers.
            const groups = new Map();
            for (const sop of sops) {
              const key = sop.category || "(uncategorized)";
              if (!groups.has(key)) groups.set(key, []);
              groups.get(key).push(sop);
            }
            const groupKeys = [...groups.keys()].sort();

            return React.createElement("div", {
              style: { display: "flex", flexDirection: "column", gap: "14px" }
            },
              ...groupKeys.map(key => {
                const rows = groups.get(key);
                const collapsed = collapsedCats.has(key);
                return React.createElement("div", { key },
                  React.createElement("button", {
                    onClick: () => toggleCat(key),
                    style: {
                      width: "100%", textAlign: "left", padding: "8px 12px",
                      background: C.warmWhite, border: `1px solid ${C.warmGray}`,
                      borderRadius: collapsed ? "8px" : "8px 8px 0 0",
                      borderBottom: collapsed ? `1px solid ${C.warmGray}` : "none",
                      cursor: "pointer", display: "flex", alignItems: "center", gap: "8px",
                      fontSize: "12px", fontWeight: 600, color: C.navy,
                      letterSpacing: "0.03em", textTransform: "uppercase",
                    }
                  },
                    React.createElement("span", {
                      style: { fontSize: "10px", opacity: 0.7, width: "10px" }
                    }, collapsed ? "▸" : "▾"),
                    key,
                    React.createElement("span", {
                      style: { color: C.textMuted, fontWeight: 500, textTransform: "none" }
                    }, `(${rows.length})`),
                  ),
                  !collapsed && React.createElement("div", null,
                    ...rows.map((sop, i) => renderRow(sop, {
                      inGroup: true,
                      isLastInGroup: i === rows.length - 1,
                      roundBottom: i === rows.length - 1,
                    }))
                  ),
                );
              })
            );
          })(),

    // Editor modal
    editor && _renderEditor(editor, {
      onChange: (updates) => setEditor({ ...editor, form: { ...editor.form, ...updates } }),
      onClose: () => !saving && setEditor(null),
      onSave: save,
      saving,
    }),

    // Viewer modal (read-only, larger view of full body)
    viewing && _renderViewer(viewing, {
      onClose: () => setViewing(null),
      canEdit: canWrite,
      onEdit: () => { openEdit(viewing); setViewing(null); },
      showNotification,
    }),
  );
}


// ─── Editor modal ─────────────────────────────────────────

function _renderEditor(editor, handlers) {
  const { mode, form } = editor;
  const set = (k) => (e) => handlers.onChange({ [k]: e.target.value });

  return React.createElement("div", {
    onClick: (e) => { if (e.target === e.currentTarget) handlers.onClose(); },
    style: {
      position: "fixed", inset: 0, background: "rgba(27, 42, 74, 0.6)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "40px 20px",
    }
  },
    React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px", padding: "24px",
        maxWidth: "800px", width: "100%", maxHeight: "90vh",
        display: "flex", flexDirection: "column",
      }
    },
      React.createElement("div", {
        style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }
      },
        React.createElement("h3", {
          style: { fontFamily: "'Playfair Display',serif", fontSize: "20px", color: C.navy, margin: 0 }
        }, mode === "new" ? "New SOP" : "Edit SOP"),
        React.createElement("button", {
          onClick: handlers.onClose,
          style: { border: "none", background: "transparent", fontSize: "20px", color: C.textMuted, cursor: "pointer" }
        }, "×"),
      ),

      React.createElement("div", { style: { flex: 1, overflowY: "auto", paddingRight: "4px" } },
        React.createElement("div", { style: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" } },
          _labeled("Title *", React.createElement("input", {
            type: "text", value: form.title, onChange: set("title"),
            placeholder: "Bankruptcy Check Process",
            style: _inputStyle,
          })),
          _labeled("Category", React.createElement("input", {
            type: "text", value: form.category, onChange: set("category"),
            placeholder: "intake · litigation · settlement · billing · general",
            style: _inputStyle,
          })),
        ),
        _labeled("Tags", React.createElement("input", {
          type: "text", value: form.tags, onChange: set("tags"),
          placeholder: "comma-separated: pacer, bankruptcy, sop-13",
          style: _inputStyle,
        })),
        // Substep 5-ζ.1: matter-stage tags. Multi-select checkboxes so
        // an SOP can apply to more than one stage (bankruptcy check runs
        // at intake AND at settlement). No selections = cross-cutting
        // (surfaced on every stage's dashboard).
        React.createElement("div", { style: { marginTop: "12px" } },
          _labeled("Applies to matter stages",
            React.createElement("div", {
              style: {
                display: "flex", flexWrap: "wrap", gap: "8px",
                padding: "8px 10px", background: C.warmWhite,
                border: `1px solid ${C.warmGray}`, borderRadius: "6px",
              },
            },
              ...STAGES.map(s => {
                const on = (form.applicable_stages || []).includes(s.key);
                return React.createElement("label", {
                  key: s.key,
                  style: {
                    display: "flex", alignItems: "center", gap: "5px",
                    padding: "3px 8px", borderRadius: "999px", cursor: "pointer",
                    background: on ? C.navy : "#fff",
                    color: on ? "#fff" : C.textDark,
                    border: `1px solid ${on ? C.navy : C.warmGray}`,
                    fontSize: "12px", userSelect: "none",
                  }
                },
                  React.createElement("input", {
                    type: "checkbox", checked: on,
                    onChange: (e) => {
                      const cur = new Set(form.applicable_stages || []);
                      if (e.target.checked) cur.add(s.key); else cur.delete(s.key);
                      handlers.onChange({ applicable_stages: [...cur] });
                    },
                    style: { margin: 0, cursor: "pointer" },
                  }),
                  s.label,
                );
              }),
              React.createElement("span", {
                style: { fontSize: "11px", color: C.textMuted, alignSelf: "center",
                         marginLeft: "auto" },
              }, (form.applicable_stages || []).length === 0
                  ? "no stages selected → applies to all"
                  : `${form.applicable_stages.length}/6 stages`),
            )
          ),
        ),
        React.createElement("div", { style: { marginTop: "12px" } },
          _labeled("Body (markdown) *", React.createElement("textarea", {
            value: form.body, onChange: set("body"),
            rows: 18,
            placeholder: "# Overview\n\nWhen a new PI matter comes in, we...\n\n## Step 1\n\n1. Go to PACER\n2. ...",
            style: {
              ..._inputStyle, minHeight: "300px", fontFamily: "monospace",
              fontSize: "12px", lineHeight: 1.5, resize: "vertical",
            }
          })),
        ),
      ),

      React.createElement("div", {
        style: {
          display: "flex", justifyContent: "flex-end", gap: "8px",
          marginTop: "16px", paddingTop: "16px", borderTop: `1px solid ${C.warmGray}`,
        }
      },
        React.createElement("button", {
          onClick: handlers.onClose,
          disabled: handlers.saving,
          style: {
            padding: "8px 16px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
            background: "#fff", color: C.textMuted, fontSize: "13px",
            cursor: handlers.saving ? "default" : "pointer",
          }
        }, "Cancel"),
        React.createElement("button", {
          onClick: handlers.onSave,
          disabled: handlers.saving || !form.title.trim() || !form.body.trim(),
          style: {
            padding: "8px 20px", borderRadius: "6px", border: "none",
            background: (handlers.saving || !form.title.trim() || !form.body.trim())
              ? C.warmGray : C.gold,
            color: "#fff", fontSize: "13px", fontWeight: 600,
            cursor: (handlers.saving || !form.title.trim() || !form.body.trim())
              ? "default" : "pointer",
          }
        }, handlers.saving ? "Saving…" : (mode === "new" ? "Create SOP" : "Save changes")),
      ),
    ),
  );
}


// ─── Viewer modal (read-only) ─────────────────────────────

function _renderViewer(sop, handlers) {
  return React.createElement("div", {
    onClick: (e) => { if (e.target === e.currentTarget) handlers.onClose(); },
    style: {
      position: "fixed", inset: 0, background: "rgba(27, 42, 74, 0.6)",
      display: "flex", alignItems: "center", justifyContent: "center",
      zIndex: 1000, padding: "40px 20px",
    }
  },
    React.createElement("div", {
      style: {
        background: "#fff", borderRadius: "12px", padding: "24px",
        maxWidth: "800px", width: "100%", maxHeight: "90vh",
        display: "flex", flexDirection: "column",
      }
    },
      React.createElement("div", {
        style: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }
      },
        React.createElement("div", { style: { flex: 1, minWidth: 0 } },
          React.createElement("h3", {
            style: { fontFamily: "'Playfair Display',serif", fontSize: "22px", color: C.navy, margin: 0 }
          }, sop.title),
          React.createElement("div", {
            style: { fontSize: "12px", color: C.textMuted, marginTop: "6px", display: "flex", gap: "10px", flexWrap: "wrap" }
          },
            sop.category && React.createElement("span", null, sop.category),
            sop.tags && React.createElement("span", null, "#" + sop.tags.split(",").join(" #")),
            sop.updated_at && React.createElement("span", null, "updated " + sop.updated_at.slice(0, 10)),
          ),
        ),
        React.createElement("div", { style: { display: "flex", gap: "6px" } },
          sop.has_original_file && React.createElement("a", {
            href: `/api/firm-sops/${sop.id}/download-original`,
            title: `Download original: ${sop.file_filename}`,
            style: {
              padding: "6px 12px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
              background: "#fff", color: C.textDark, fontSize: "12px",
              fontWeight: 600, cursor: "pointer", textDecoration: "none",
            }
          }, "Download original"),
          !sop.has_original_file && sop.file_filename && React.createElement("span", {
            style: {
              padding: "6px 12px", borderRadius: "6px",
              background: C.warmWhite, color: C.textMuted,
              fontSize: "11px", fontStyle: "italic",
            },
            title: `Original file was too large to store (${sop.file_size_bytes ? (sop.file_size_bytes / 1024 / 1024).toFixed(1) + " MB" : "over 25 MB"})`,
          }, `From ${sop.file_filename} (original not stored)`),
          handlers.canEdit && React.createElement("button", {
            onClick: handlers.onEdit,
            style: {
              padding: "6px 12px", borderRadius: "6px", border: `1px solid ${C.warmGray}`,
              background: "#fff", color: C.textDark, fontSize: "12px",
              fontWeight: 600, cursor: "pointer",
            }
          }, "Edit"),
          React.createElement("button", {
            onClick: handlers.onClose,
            style: { border: "none", background: "transparent", fontSize: "20px", color: C.textMuted, cursor: "pointer" }
          }, "×"),
        ),
      ),

      // Substep 5-θ.3: SopBodyRenderer parses {{creds.*}} tokens as
      // reveal chips. Firm SOPs page has no matter context.
      React.createElement("div", {
        style: {
          flex: 1, overflowY: "auto", padding: "16px",
          background: C.warmWhite, borderRadius: "8px",
        }
      },
        React.createElement(SopBodyRenderer, {
          body: sop.body || "",
          matterId: null,
          showNotification: handlers.showNotification,
        }),
      ),
    ),
  );
}


// ─── Small helpers ────────────────────────────────────────

const _inputStyle = {
  width: "100%", padding: "8px 10px", border: `1px solid #E8E4DD`,
  borderRadius: "6px", fontSize: "13px", boxSizing: "border-box",
};

function _labeled(label, control) {
  return React.createElement("div", null,
    React.createElement("label", {
      style: { display: "block", fontSize: "12px", fontWeight: 600, color: "#1B2A4A", marginBottom: "4px" }
    }, label),
    control,
  );
}
